import type { VisibilityState } from "@tanstack/react-table";

import type { BillingSettings } from "~/modules/billing/api/billingSettings/billingSettingsTypes.ts";
import {
	type BillingSettingsDivergingFields,
	type BillingSettingsLookupData,
	computeBillingSettingsDifferences,
	type TableLink,
} from "~/modules/billing/utils/billingSettingsDisplayUtils.ts";
import type { OrderWithBillingSettings } from "~/modules/project/api/order/orderTypes.ts";
import type { ProjectPhase } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import { getPhaseOrderPath } from "~/modules/project/utils/projectDetailsViewUtils.ts";
import { getFullPhaseTitle } from "~/modules/project/utils/projectPhaseUtils.ts";

export type DivergingOrderBillingSettingsRow = BillingSettingsDivergingFields & {
	orderId: string;
	phaseAndOrderName: TableLink;
};

export function buildDivergingOrderBillingSettingsTableData(
	orders: OrderWithBillingSettings[],
	phases: ProjectPhase[],
	projectBillingSettings: BillingSettings,
	lookups: BillingSettingsLookupData,
	clientId: string,
): DivergingOrderBillingSettingsRow[] {
	const phasesById = new Map(phases.map((p) => [p.id, p]));
	const rows: DivergingOrderBillingSettingsRow[] = [];

	orders
		.filter(
			(order): order is OrderWithBillingSettings & { billingSettings: BillingSettings } =>
				order.billingSettings !== null,
		)
		.forEach((order) => {
			const divergingFields = computeBillingSettingsDifferences(order.billingSettings, projectBillingSettings, lookups, clientId);
			if (!Object.values(divergingFields).some((field) => field.isDifferent)) {
				return;
			}
			const phase = phasesById.get(order.projectPhaseId)!;

			rows.push({
				orderId: order.id,
				phaseAndOrderName: {
					displayName: `${getFullPhaseTitle(phase)} - ${order.title}`,
					url: getPhaseOrderPath(phase.projectId, phase.phaseNumber, order.id, "billing-settings"),
				},
				...divergingFields,
			});
		});
	return rows;
}

const DIFFABLE_COLUMNS = [
	"invoiceTypeId",
	"isFixedPrice",
	"isSkillBased",
	"travelExpensesIncluded",
	"isBilledMonthly",
	"paymentTargetDays",
	"currencyDisplayName",
	"resolvedInvoiceRecipient",
	"invoiceByMail",
	"additionalEmailRecipients",
	"billingInstructions",
] as const satisfies readonly (keyof DivergingOrderBillingSettingsRow)[];

export function computeColumnVisibility(rows: DivergingOrderBillingSettingsRow[]): VisibilityState {
	const visibility: VisibilityState = {};

	for (const column of DIFFABLE_COLUMNS) {
		visibility[column] = rows.some((row) => row[column].isDifferent);
	}

	return visibility;
}